// CSV export — ONE shared module for every office screen that exports
// (D-248, lifted from module 06's recall pack and movements ledger in P3-D
// PR-D5, D-289 ruling 11): the recall pack, the movements ledger, the waste
// ledger, the calibration records, and the sales customers, quotes and orders
// lists.
//
// The export is built from the rows the caller ALREADY holds — it is a
// serialization of what is on screen, never a second, wider read. That matters
// for §7: a money column the server withheld is absent from the screen and
// therefore absent from the file; a CSV can never become a redaction side door.
//
// Pure string building + one object-URL download, split so the escaping is
// unit-tested without touching the DOM.

// ---------------------------------------------------------------------------
// THE LOCALE CONVENTION (D-294). A CSV export is a spreadsheet document written
// for the person who pressed Export, and three of this module's own choices
// already said so before anyone asked: the UTF-8 BOM exists for exactly one
// reader, the formula guard exists because a spreadsheet evaluates cells, and
// nothing in the product re-imports an export. So the delimiter and the decimal
// separator follow that reader's locale.
//
// DERIVED FROM ONE PROBE, NEVER TABULATED. `Intl.NumberFormat(locale)` gives
// the locale's decimal separator — the idiom already used in
// areas/office/production/lib/format.ts and areas/floor-pwa/lib/qty-entry.ts.
// When that separator is a comma the list separator is a semicolon, otherwise a
// comma. That is the rule every spreadsheet uses, and deriving it means there is
// no locale table to drift as languages are added: ru and fr take the semicolon
// today, en and zh the comma, and a locale added tomorrow classifies itself.
//
// WHAT DOES NOT MOVE (D-289 ruling 3 stands): money is the integer minor-unit
// value beside a currency column, which reads as a number in EVERY locale — that
// is why that ruling chose it, so there is nothing to fix and a decimal point
// would be an invention. Instants stay ISO, ids and enums and booleans and the
// header keys stay wire, and the BOM and CRLF are already right everywhere.
// ---------------------------------------------------------------------------

/** How one reader's spreadsheet expects a CSV to be punctuated. */
export interface CsvConvention {
  /** The field separator: "," or ";". */
  readonly delimiter: string;
  /** The decimal mark inside a numeric quantity: "." or ",". */
  readonly decimal: string;
}

/**
 * The comma/dot pair. NOT a default: `toCsv` requires a convention, so every
 * producer has to decide whose spreadsheet it is writing for and none can
 * inherit the old behaviour by forgetting. It is here to be NAMED — by the en
 * and zh paths, which genuinely want it, and by the tests.
 */
export const CSV_COMMA_DOT: CsvConvention = { delimiter: ",", decimal: "." };

const conventions = new Map<string, CsvConvention>();

/**
 * The convention for a locale. Cached per locale, and an unknown locale
 * degrades to the canonical dot/comma pair — the same degradation
 * `production/lib/format.ts` already takes.
 */
export function csvConventionFor(locale: string | undefined): CsvConvention {
  const key = locale ?? "";
  const cached = conventions.get(key);
  if (cached !== undefined) return cached;
  let decimal = ".";
  try {
    decimal =
      new Intl.NumberFormat(locale).formatToParts(1.1).find((part) => part.type === "decimal")
        ?.value ?? ".";
  } catch {
    // Unknown locale — keep the canonical pair rather than guess.
  }
  const convention: CsvConvention = { decimal, delimiter: decimal === "," ? ";" : "," };
  conventions.set(key, convention);
  return convention;
}

/**
 * A decimal QUANTITY, re-punctuated for the reader without being re-derived.
 *
 * The swap is a single character substitution on the digits the server sent, so
 * `49.400` becomes `49,400` with its trailing zeros intact and a 6-dp off-cut
 * measure keeps all six places. It never parses to a JavaScript number:
 * `Number()` would drop the trailing zeros and cap precision at a double, which
 * is the loss the calibration export's "exact decimal strings" note guards
 * against.
 *
 * OPT IN PER COLUMN, never inferred from a cell's shape — a lot code, a serial,
 * a PO number and a version string can all look like decimals, and a guesser
 * would mangle them.
 */
export function csvDecimal(text: string, convention: CsvConvention): string {
  return convention.decimal === "." ? text : text.replace(".", convention.decimal);
}

/** RFC 4180 field escaping, plus the spreadsheet formula-injection guard. */
export function csvCell(
  value: string | number | null | undefined,
  convention: CsvConvention,
): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  // A leading =, +, - or @ makes Excel/Sheets evaluate the cell as a formula,
  // and a leading TAB or CR is the known bypass of that check (OWASP's CSV
  // injection list; D-289 ruling 3). Customer names and PO numbers arrive
  // from outside — Telegram triage, customer emails — so the guard is not
  // theoretical. Prefixing an apostrophe keeps the text readable and inert.
  // A plain signed decimal ("-12.500", "+3") is a NUMBER and cannot carry a
  // formula; guarding it would make a spreadsheet read it as text and skip it
  // in SUM/AVERAGE without a word (a negative calibration variance is the
  // common case — D-289 ruling 3, amended in review).
  // The plain-number test follows the ACTIVE decimal mark. Under a comma
  // decimal it must accept "-12,500", or D-289's review amendment — a negative
  // variance must stay a number rather than apostrophe-guarded text a
  // spreadsheet silently skips in SUM — regresses the moment the locale moves.
  const decimal = convention.decimal === "." ? "\\." : ",";
  const plainNumber = new RegExp(`^[+-]?\\d+(${decimal}\\d+)?$`, "u").test(text);
  const guarded = !plainNumber && /^[=+\-@\t\r]/u.test(text) ? `'${text}` : text;
  // Quote on the ACTIVE delimiter, never a hardcoded comma: under a semicolon
  // file a comma is ordinary text (it is the decimal mark), and a semicolon is
  // what must be quoted.
  const mustQuote = guarded.includes(convention.delimiter) || /["\n\r]/u.test(guarded);
  return mustQuote ? `"${guarded.replace(/"/gu, '""')}"` : guarded;
}

/**
 * Build a CSV document from a header row + body rows.
 *
 * `decimalHeaders` names the columns whose cells are decimal QUANTITIES, by
 * their header text rather than by index, so inserting a column cannot silently
 * re-point the treatment at the wrong one. Headers are wire key names
 * (D-289 ruling 3), which makes them stable to name here.
 */
export function toCsv(
  header: readonly string[],
  rows: readonly (readonly (string | number | null | undefined)[])[],
  options: { convention: CsvConvention; decimalHeaders?: readonly string[] },
): string {
  const { convention } = options;
  const decimalAt = new Set(
    (options.decimalHeaders ?? [])
      .map((name) => header.indexOf(name))
      .filter((index) => index >= 0),
  );
  const cell = (value: string | number | null | undefined, column: number): string => {
    if (decimalAt.has(column) && (typeof value === "string" || typeof value === "number")) {
      return csvCell(csvDecimal(String(value), convention), convention);
    }
    return csvCell(value, convention);
  };
  const lines = [header.map((h) => csvCell(h, convention)).join(convention.delimiter)];
  for (const row of rows) lines.push(row.map(cell).join(convention.delimiter));
  return `${lines.join("\r\n")}\r\n`;
}

/**
 * Trigger a browser download of `content` as `filename`. Uses a UTF-8 BOM so
 * Excel opens Cyrillic/CJK exports correctly — the design partner is
 * Russian-first and a mojibake recall pack is a useless recall pack.
 */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([`\uFEFF${content}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// The FULL-RESULT walk (D-248, D-289 ruling 1). An export of "the list" must be
// the list the filters describe, never the pages someone happened to scroll —
// the waste ledger's month-end file once claimed the month and contained 50
// rows (GM-week audit, 2026-08). The walk reads the SAME cursor endpoint the
// screen reads, with the screen's own filters and sort, until the server says
// it is done or the honesty cap bites.
// ---------------------------------------------------------------------------

/** The biggest page every exporting list read allows (`limit` max 200). */
export const EXPORT_PAGE_SIZE = 200;

/** The HONESTY CAP. A walk with no ceiling is a tab-freezing, API-hammering
 * footgun on a filter someone forgot to narrow. When it bites, the file still
 * holds exactly `EXPORT_ROW_CAP` rows, the walk STOPS, and the file and the
 * screen both say "the first N; more exist". A partial file that looks
 * complete is the one outcome this exists to prevent. */
export const EXPORT_ROW_CAP = 10_000;

/** One cursor page, as every list read answers it. */
export interface CursorPage<T> {
  items: readonly T[];
  next_cursor?: string | null;
}

/**
 * Walk a cursor-paginated read to exhaustion or to `cap` rows. `fetchPage` is
 * called with the cursor (undefined for the first page) and the page size, and
 * must send the screen's exact filters and sort — a cursor minted under one
 * sort is refused under another. `capped: true` means rows exist beyond the
 * file (never "exactly the cap happened to remain"). An empty page is treated
 * as exhaustion even beside a non-null cursor: that shape is a server defect,
 * and honouring it would spin forever. A rejected page rejects the walk, and
 * the caller downloads NOTHING — half a file is worse than none.
 */
export async function walkCursorForExport<T>(
  fetchPage: (cursor: string | undefined, limit: number) => Promise<CursorPage<T>>,
  options: { pageSize?: number; cap?: number; onProgress?: (loaded: number) => void } = {},
): Promise<{ rows: T[]; capped: boolean }> {
  const pageSize = options.pageSize ?? EXPORT_PAGE_SIZE;
  const cap = options.cap ?? EXPORT_ROW_CAP;
  const rows: T[] = [];
  let cursor: string | undefined;
  for (;;) {
    const page = await fetchPage(cursor, pageSize);
    for (const row of page.items) {
      // A row exists that the file will not carry — the cap has bitten.
      if (rows.length === cap) return { rows, capped: true };
      rows.push(row);
    }
    options.onProgress?.(rows.length);
    if (page.next_cursor == null || page.items.length === 0) return { rows, capped: false };
    // The server promises more (`next_cursor` non-null) but the file is full.
    if (rows.length === cap) return { rows, capped: true };
    cursor = page.next_cursor;
  }
}

/**
 * The HONESTY ROW (D-248, D-289 ruling 13): line 2 of every export, one cell
 * starting `# `, padded to the header's width so a spreadsheet keeps its
 * columns. It carries what the file is — whose data, which list, the scope,
 * the filters, the sort, when, and whether it is complete — because the file
 * is what gets forwarded, and a file with no provenance reads as "everything".
 * `parts` are already-localized phrases; empty ones are dropped.
 */
export function honestyRow(parts: readonly string[], width: number): string[] {
  const text = parts
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
    .join(" · ");
  const row = [`# ${text}`];
  for (let i = 1; i < width; i++) row.push("");
  return row;
}
